import type { ReactNode } from 'react';
import type { OasisValidationError } from '../../services/api/types.gen';
import {
  buildValidationCardFromPresentation,
  formatConflictingFieldCurrent,
  type ValidationCardSupporting,
} from './validationPresentation';
import { formatValidationDescription } from './cmsFieldErrors';

const VALIDATION_HIGHLIGHT_PATTERN =
  /"[^"]+"|\b[A-Z]\d{4}_[A-Z0-9_]+\b|\b(must|cannot|requires|blank|Yes|No)\b/gi;

const splitValidationRule = (text: string) => {
  const ifThenMatch = text.match(/^If\s+(.+?)(?:,\s*then|\s+then)\s+(.+)$/i);
  if (ifThenMatch) {
    return {
      type: 'ifThen' as const,
      condition: ifThenMatch[1].trim(),
      expectation: ifThenMatch[2].trim(),
    };
  }

  const requiresMatch = text.match(/^(.+?)\s+requires\s+(.+)$/i);
  if (requiresMatch) {
    return {
      type: 'paired' as const,
      condition: requiresMatch[1].trim(),
      expectation: requiresMatch[2].trim(),
    };
  }

  const colonIdx = text.indexOf(':');
  if (colonIdx > 0) {
    return {
      type: 'paired' as const,
      condition: text.slice(0, colonIdx).trim(),
      expectation: text.slice(colonIdx + 1).trim(),
    };
  }

  return { type: 'single' as const, condition: '', expectation: text };
};

const parseValidationDetail = (detail?: string) => {
  if (!detail) return null;
  const trimmed = detail.trim();
  if (!trimmed) return null;

  const notInMatch = trimmed.match(/^'?(.*?)'?\s+not in:\s+(.+)$/i);
  if (notInMatch) {
    const entered = notInMatch[1]?.trim();
    const allowedPart = notInMatch[2] || '';
    const allowedCodes = Array.from(
      new Set(
        [
          // Each entry is `CODE (LABEL)` separated by ', '. Anchor to the
          // start/after-comma so we don't match inside a label, and allow one
          // level of nested parens so e.g. 'Not checked (No)' and
          // 'Saipan (Northern Mariana Islands)' aren't truncated.
          ...allowedPart.matchAll(
            /(?:^|,\s*)([A-Z0-9-]{1,4})\s*\(((?:[^()]|\([^()]*\))*)\)/g
          ),
        ].map(m => `${m[1]} - ${m[2]}`)
      )
    );
    return {
      type: 'notIn' as const,
      entered,
      allowedCodes,
      raw: trimmed,
    };
  }

  const assignmentMatch = trimmed.match(/^"?(.*?)"?\s*=\s*(.+)$/);
  if (assignmentMatch) {
    return {
      type: 'assignment' as const,
      label: assignmentMatch[1]?.trim(),
      value: assignmentMatch[2]?.trim(),
      raw: trimmed,
    };
  }

  return { type: 'text' as const, raw: trimmed };
};

const AT_LEAST_ONE_RE =
  /\bat least one\b|mark all that apply|choose at least one/i;

const isBlankishValue = (v: string) => {
  const t = v.toLowerCase().trim();
  return t === '' || t === 'blank' || t === '^';
};

// A "choose at least one" group with nothing checked: the values come back as
// "0" / "No" / "Not checked (No)" rather than truly blank, but it's still an
// empty selection from the user's point of view.
const isNoneSelectedValue = (v: string) => {
  const t = v.toLowerCase().trim();
  return (
    isBlankishValue(v) || t === '0' || t === 'no' || t === 'not checked (no)'
  );
};

/**
 * Temporary fallback for validation errors without backend `presentation`.
 * New rule shapes should get explicit presenters on the backend instead of
 * growing this function.
 */
const buildValidationFallbackReason = (
  description: string,
  detail: string | undefined,
  detailInfo: ReturnType<typeof parseValidationDetail>,
  values: string[],
  headline: string,
  oasisFields: string[] = [],
  oasisFieldLabels: Record<string, string> | undefined = undefined
) => {
  const isAtLeastOne = AT_LEAST_ONE_RE.test(`${headline} ${description}`);
  const isEmpty =
    values.length > 0 &&
    (values.every(isBlankishValue) ||
      (isAtLeastOne && values.every(isNoneSelectedValue)));

  if (isEmpty) {
    return {
      emptyLabel: isAtLeastOne ? 'None selected' : 'Not answered',
      primary: null as string | null,
      current: null as string | null,
      allowedCodes: [] as string[],
    };
  }

  const raw = (detail || '').trim();
  let primary: string | null = description;
  // Only surface the entered value when it's a single field's value. For
  // multi-field consistency rules the joined list of mixed values
  // ("blank, Checked (Yes), …") is noise — the rule sentence is the message.
  let current: string | null = values.length === 1 ? values[0] : null;
  let m: RegExpMatchArray | null;

  // Numeric constraints: fold the numbers into one human phrase (no "len",
  // and the entered value is already in the phrase so drop the Current line).
  if ((m = raw.match(/^len (\d+) > max (\d+)$/))) {
    primary = `Too long — ${m[1]} characters (max ${m[2]})`;
    current = null;
  } else if ((m = raw.match(/^len (\d+) != (\d+)$/))) {
    primary = `Must be exactly ${m[2]} characters (currently ${m[1]})`;
    current = null;
  } else if ((m = raw.match(/^(-?\d+) > max (-?\d+)$/))) {
    primary = `Too high — max ${m[2]} (entered ${m[1]})`;
    current = null;
  } else if ((m = raw.match(/^(-?\d+) < min (-?\d+)$/))) {
    primary = `Too low — min ${m[2]} (entered ${m[1]})`;
    current = null;
  } else if (detailInfo?.type === 'notIn') {
    // Wrong coded value: the entered value + the allowed set are the two
    // useful facts; the generic "pick an option" rule text is redundant.
    primary = null;
  }

  const allowedCodes =
    detailInfo?.type === 'notIn' ? detailInfo.allowedCodes : [];

  const { current: resolvedCurrent, currentPrefix } =
    formatConflictingFieldCurrent(current, oasisFields, oasisFieldLabels);

  return {
    emptyLabel: null as string | null,
    primary,
    current: resolvedCurrent,
    currentPrefix,
    allowedCodes,
  };
};

/** Map fallback reason shape to card supporting-line props. */
const toValidationCardSupporting = (
  reason: ReturnType<typeof buildValidationFallbackReason>,
  hasQuestionHeadline: boolean
): ValidationCardSupporting => {
  const primaryText = hasQuestionHeadline ? reason.primary : null;
  const allowedPreview = reason.allowedCodes.slice(0, 8).join(', ');
  const allowedExtra = Math.max(0, reason.allowedCodes.length - 8);
  return {
    emptyLabel: reason.emptyLabel,
    primaryText,
    current: reason.current,
    currentPrefix: reason.currentPrefix,
    currentVariant: reason.currentPrefix === 'Also checked' ? 'text' : 'code',
    allowedPreview,
    allowedVariant: 'code',
    allowedExtra,
    hasSupporting:
      !!reason.emptyLabel ||
      !!primaryText ||
      !!reason.current ||
      !!allowedPreview,
  };
};

const highlightValidationText = (text: string) => {
  const matches = [...text.matchAll(VALIDATION_HIGHLIGHT_PATTERN)];
  if (matches.length === 0) return text;

  const nodes: ReactNode[] = [];
  let cursor = 0;
  matches.forEach((match, idx) => {
    const start = match.index ?? 0;
    const value = match[0];
    if (start > cursor) {
      nodes.push(text.slice(cursor, start));
    }
    nodes.push(
      <span key={`hl-${idx}-${start}`} className='font-medium text-foreground'>
        {value}
      </span>
    );
    cursor = start + value.length;
  });
  if (cursor < text.length) {
    nodes.push(text.slice(cursor));
  }
  return nodes;
};

export interface CmsIssueCardProps {
  error: OasisValidationError;
  qidToQuestionText: Record<string, string>;
  /** Omitted where there is no form to jump into: the card is read-only. */
  onJump?: (error: OasisValidationError) => void;
}

export function CmsIssueCard({
  error,
  qidToQuestionText,
  onJump,
}: CmsIssueCardProps) {
  const formatted = formatValidationDescription(error.description || '');
  const {
    type: ruleType,
    condition,
    expectation,
  } = splitValidationRule(formatted);
  const questionTexts = Array.from(
    new Set(
      (error.qids || []).map(qid => qidToQuestionText[qid]).filter(Boolean)
    )
  );
  const isFatal = error.severity === 'fatal';
  const detailInfo = parseValidationDetail(error.detail);
  const valueItems = (error.values || []).filter(Boolean);

  // A rule-authored message wins over the schema question text: the question is
  // written for the person answering it and may carry answer-format
  // instructions that read as noise in the issue list (EMR-1248).
  const headline =
    error.scrubber_message ||
    questionTexts[0] ||
    formatted ||
    'Validation issue';
  const hasQuestionHeadline =
    !!error.scrubber_message || questionTexts.length > 0;
  // For If/Then consistency rules the relationship IS the
  // message; otherwise lean on the friendly rule sentence.
  const ruleSentence =
    ruleType === 'ifThen' ? `If ${condition} → ${expectation}` : formatted;

  const fromPresentation = buildValidationCardFromPresentation(
    error,
    hasQuestionHeadline
  );
  const reason =
    fromPresentation ??
    toValidationCardSupporting(
      buildValidationFallbackReason(
        ruleSentence,
        error.detail,
        detailInfo,
        valueItems,
        headline,
        error.oasis_fields ?? [],
        error.oasis_field_labels
      ),
      hasQuestionHeadline
    );
  const {
    emptyLabel,
    primaryText,
    current,
    currentPrefix = 'Current',
    currentVariant = 'code',
    allowedPreview,
    allowedVariant = 'code',
    allowedExtra,
    hasSupporting,
  } = reason;

  // Read-only (no form to jump into): a plain block, so assistive tech reads
  // the whole card rather than a disabled button's label.
  const Root = onJump ? 'button' : 'div';
  return (
    <Root
      {...(onJump && {
        type: 'button' as const,
        onClick: () => onJump(error),
        'aria-label': `${isFatal ? 'Fatal' : 'Warning'}: ${headline}`,
      })}
      className={`relative w-full rounded-md border bg-background/80 px-2.5 py-1.5 text-left text-xs ${
        onJump
          ? 'group cursor-pointer transition-colors hover:border-foreground/30'
          : ''
      }`}
    >
      <div className='flex items-start gap-2'>
        <span
          title={isFatal ? 'Fatal' : 'Warning'}
          className={`mt-[5px] h-1.5 w-1.5 shrink-0 rounded-full ${
            isFatal ? 'bg-destructive' : 'bg-amber-500'
          }`}
        />
        <div className='min-w-0 flex-1'>
          <p className='font-medium leading-snug text-foreground/90'>
            {headline}
          </p>
          {hasSupporting && (
            <p className='mt-0.5 leading-snug text-muted-foreground'>
              {emptyLabel ? (
                <span className='font-medium text-foreground/70'>
                  {emptyLabel}
                </span>
              ) : (
                <>
                  {primaryText && (
                    <span>
                      {fromPresentation
                        ? primaryText
                        : highlightValidationText(primaryText)}
                    </span>
                  )}
                  {current && (
                    <>
                      {primaryText && (
                        <span className='px-1 text-foreground/25'>·</span>
                      )}
                      <span>
                        {currentPrefix}:{' '}
                        <span
                          className={
                            currentVariant === 'code'
                              ? 'font-mono text-foreground/80'
                              : 'text-foreground/80'
                          }
                        >
                          {current}
                        </span>
                      </span>
                    </>
                  )}
                  {allowedPreview && (
                    <>
                      {(primaryText || current) && (
                        <span className='px-1 text-foreground/25'>·</span>
                      )}
                      <span>
                        Allowed:{' '}
                        <span
                          className={
                            allowedVariant === 'code'
                              ? 'font-mono text-foreground/70'
                              : 'text-foreground/70'
                          }
                        >
                          {allowedPreview}
                        </span>
                        {allowedExtra > 0 && ` +${allowedExtra}`}
                      </span>
                    </>
                  )}
                </>
              )}
            </p>
          )}
        </div>
        <span className='shrink-0 select-none font-mono text-[10px] text-muted-foreground/40 opacity-0 transition-opacity group-hover:opacity-100'>
          {error.edit_id}
        </span>
      </div>
    </Root>
  );
}
